/**
 * "View NAR grid" menu action (PA form variant): opens modal and optionally
 * fetches auth grid source. Driven by Postgres-backed `generalOrderDetails`
 * and `authOnFile`.
 *
 * @module paFormThreeDotsMenu/handleViewNarGrid
 */
import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { MutableRefObject } from "react";
import { getAuthGridSource } from "../../../../api/postCall/getAuthGridSource";
import {
  AuthOnFile,
  GeneralOrderDetails,
} from "../../../../data-model/medicalPaOrdersModel";
import { ScreenNamesForBigQuery } from "../../../../enums/eventsData";
import { EventsName } from "../../../../enums/eventsName";
import { ModalId } from "../../../../enums/modalId";
import {
  closeModal,
  setMetaData,
  setOpenedModalId,
} from "../../../../redux/slice/modalSlice";
import { AppDispatch } from "../../../../redux/store/store";
import { apiWorkerManager } from "../../../../workerManagers/workerManager";
import { ToastMessages } from "../../../toasts/toastMessages";

/** Parameters for {@link handleViewNarGrid}. */
export interface HandleViewNarGridParams {
  dispatch: AppDispatch;
  generalOrderDetails: GeneralOrderDetails;
  authOnFile: AuthOnFile;
  abortControllerRef: MutableRefObject<AbortController | null>;
  orgId: string;
  userId: string;
  userEmail: string;
  payerName: string;
  screenName: ScreenNamesForBigQuery;
}

/**
 * Extracts auth grid source and the newest `created_at` from nested
 * auth-on-file entries.
 *
 * @param authOnFile - Auth-on-file payload for the order, or null
 * @returns First non-empty grid `source` found (if any) and latest
 *   `created_at` among grid rows
 */
const extractAuthGridData = (
  authOnFile: AuthOnFile | null,
): { foundSource: string; latestCreatedAt: string } => {
  let foundSource = "";
  const createdAtDates: string[] = [];

  authOnFile?.entries?.some((entry) =>
    entry.j_codes?.some((jcode) => {
      if (jcode.nar_status?.auth_grid?.created_at) {
        createdAtDates.push(jcode.nar_status.auth_grid.created_at);
      }
      if (jcode.nar_status?.auth_grid?.source) {
        foundSource = jcode.nar_status.auth_grid.source;
        return true;
      }
      return false;
    }),
  );

  const latestCreatedAt =
    createdAtDates.length > 0
      ? createdAtDates.sort(
          (a, b) => new Date(b).getTime() - new Date(a).getTime(),
        )[0]
      : "";

  return { foundSource, latestCreatedAt };
};

/**
 * Handles the "View NAR Grid" action for the PA form three-dots menu.
 * Opens the NAR grid modal and fetches auth grid source if not available.
 *
 * @param params - Parameters for the action
 * @returns Promise that resolves when modal state has been updated (fetch may
 *   still surface a toast on failure)
 */
export const handleViewNarGrid = async ({
  dispatch,
  generalOrderDetails,
  authOnFile,
  abortControllerRef,
  orgId,
  userId,
  userEmail,
  payerName,
  screenName,
}: HandleViewNarGridParams): Promise<void> => {
  const orderId = generalOrderDetails?.order_id ?? "";

  apiWorkerManager.enqueueAndProcess({
    patient_id:
      generalOrderDetails?.demographics?.patient_id ??
      generalOrderDetails?.demographics?.patient_mrn ??
      "",
    order_id: orderId,
    org_id: orgId ?? "",
    event_name: EventsName.VIEW_NAR_CLIKCED,
    user_id: userId,
    user_email: userEmail,
    additional_data: {
      screen_name: screenName,
      payer_name: payerName,
    },
  });

  if (abortControllerRef.current) {
    abortControllerRef.current.abort();
  }

  abortControllerRef.current = new AbortController();
  const signal = abortControllerRef.current.signal;

  const { foundSource, latestCreatedAt } = extractAuthGridData(
    authOnFile ?? null,
  );

  if (!foundSource) {
    dispatch(
      setOpenedModalId({
        id: ModalId.NAR_GRID_MODAL,
        metaData: {
          source: "",
          loading: true,
          created_at: latestCreatedAt,
        },
      }),
    );

    try {
      const authGridSource = await getAuthGridSource({
        order_id: orderId,
        signal: signal,
      });

      dispatch(
        setMetaData({
          source: authGridSource.source ?? "",
          loading: false,
          created_at: authGridSource.updated_at ?? latestCreatedAt,
        }),
      );

      abortControllerRef.current = null;
    } catch (error) {
      if (error instanceof Error && error.name === "AbortError") {
        dispatch(closeModal());
      } else {
        dispatch(
          setMetaData({
            source: "",
            loading: false,
            created_at: latestCreatedAt,
          }),
        );
        toast.error(ToastMessages.NAR_GRID_NOT_AVAILABLE);
        console.error("Error getting auth grid source", error);
      }
      abortControllerRef.current = null;
    }
  } else {
    dispatch(
      setOpenedModalId({
        id: ModalId.NAR_GRID_MODAL,
        metaData: {
          source: foundSource,
          loading: false,
          created_at: latestCreatedAt,
        },
      }),
    );
  }
};
